import React, { useMemo } from 'react';

import Link from '@dashboard/components/mui-atoms/Link';
import TemplateThumbnailPreview from '@dashboard/components/TemplateThumbnailPreview';
import { useStandardTemplatesListQuery } from '@dashboard/generated/wp/platform';
import useCurrentPlatformId from '@dashboard/hooks/route/useCurrentPlatformId';
import { OpenInNewOutlined } from '@mui/icons-material';
import { Box, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material';

import useWorkflowStepField from '../useWorkflowStepField';

type Props = {
  name: string;
};
export default function TemplateSelectField({ name }: Props) {
  const platformId = useCurrentPlatformId();
  const {
    input: { onChange, value },
  } = useWorkflowStepField(name);
  const { data, loading } = useStandardTemplatesListQuery({ fetchPolicy: 'network-only' });

  const onTemplateChange = (value: string) => {
    if (value === 'QUICK_TEMPLATE') {
      onChange({
        disabled: false,
        sendMarkdownEmailMessage: {
          senderType: 'VERIFIED_EMAIL_ADDRESS',
          toTemplate: '',
          subjectTemplate: '',
          messageTemplate: '',
        },
      });
    } else {
      onChange({
        disabled: false,
        sendEmailTemplateMessage: {
          templateId: value,
        },
      });
    }
  };

  const templateValue = useMemo(() => {
    if (value.sendEmailTemplateMessage) {
      return value.sendEmailTemplateMessage.templateId;
    }
    return 'QUICK_TEMPLATE';
  }, [value]);

  const helperTextValue = useMemo(() => {
    if (value.sendEmailTemplateMessage) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'end' }}>
          <Link
            target="_blank"
            href={`/o/${platformId}/templates/${value.sendEmailTemplateMessage.templateId}`}
            color="inherit"
            fontWeight="medium"
            underline="hover"
            sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
          >
            Open in template builder
            <OpenInNewOutlined sx={{ fontSize: 14 }} />
          </Link>
        </Box>
      );
    }
    return null;
  }, [value, platformId]);

  const templates = data?.WaypointTemplates.nodes ?? [];

  return (
    <TextField
      label="Template"
      fullWidth
      disabled={loading}
      size="small"
      name="templateId"
      variant="outlined"
      select
      value={templateValue}
      helperText={helperTextValue}
      onChange={(ev) => onTemplateChange(ev.target.value)}
    >
      <MenuItem value="QUICK_TEMPLATE">Use a quick template</MenuItem>
      <Divider />
      {templates.map((t) => (
        <MenuItem value={t.id} key={t.id}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 0.25 }}>
            <Box sx={{ width: 64, height: 64, border: '1px solid', borderColor: 'cadet.300' }}>
              <TemplateThumbnailPreview template={t} />
            </Box>
            <Typography>{t.name}</Typography>
          </Stack>
        </MenuItem>
      ))}
    </TextField>
  );
}
